import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { findSeriesById } from '../../data/testSeriesData';
import { collection, doc, getDocs, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { calculateTestStatus, parsePurchaseDate } from '../../utils/testStatusHelper';
import { FaArrowLeft, FaArrowRight, FaDownload } from 'react-icons/fa6';
import Toast from '../../components/Toast';
import './PurchasedTestsPage.css';
import jsPDF from 'jspdf';
import logoImg from '../../components/image/PrrepX Logo.jpg';

const PurchasedTestsPage = () => {
    const { currentUser, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    
    const [purchasedCourses, setPurchasedCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [downloadingInvoice, setDownloadingInvoice] = useState(null); // Track which invoice is downloading
    const [showBrowseModal, setShowBrowseModal] = useState(false);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!authLoading && !currentUser) {
            navigate('/login', { state: { from: '/purchased-tests' } });
        }
    }, [currentUser, authLoading, navigate]);

    useEffect(() => {
        const loadPurchasedCourses = async () => {
            if (!currentUser) return;
            
            setLoading(true);
            try {
                // Fetch user document to get purchasedCourses array
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                
                let purchasedCourseIds = [];
                
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    purchasedCourseIds = userData.purchasedCourses || [];
                }
                
                // FALLBACK: If no purchasedCourses array, fetch from transactions (for backward compatibility)
                // This handles users who purchased before the migration
                if (purchasedCourseIds.length === 0) {
                    console.log("ℹ️ No purchasedCourses array found, fetching from transactions (fallback)...");
                    const txQuery = query(
                        collection(db, 'transactions'),
                        where('userId', '==', currentUser.uid),
                        where('status', '==', 'success')
                    );
                    const txSnapshot = await getDocs(txQuery);
                    const uniqueCourseIds = new Set();
                    txSnapshot.forEach(doc => {
                        const tx = doc.data();
                        if (tx.courseId) {
                            uniqueCourseIds.add(tx.courseId);
                        }
                    });
                    purchasedCourseIds = Array.from(uniqueCourseIds);
                    console.log(`ℹ️ Found ${purchasedCourseIds.length} courses from transactions (fallback)`);
                }
                
                if (purchasedCourseIds.length === 0) {
                    setPurchasedCourses([]);
                    setLoading(false);
                    return;
                }
                
                // Now fetch transaction details for each purchased course
                const purchases = [];
                const transactionsCollection = collection(db, 'transactions');
                
                // First, identify which combos are purchased
                const activeCombos = [];
                for (const courseId of purchasedCourseIds) {
                    const series = findSeriesById(courseId);
                    if (series && series.Contains_ids && series.Contains_ids.length > 0) {
                        // This is a combo
                        const txDocId = `${currentUser.uid}_${courseId}`;
                        const txDocSnap = await getDoc(doc(transactionsCollection, txDocId));
                        if (txDocSnap.exists()) {
                            const tx = txDocSnap.data();
                            const purchasedDate = parsePurchaseDate(tx);
                            const { status } = calculateTestStatus(purchasedDate, series.ValidityYears || 2);
                            if (status === 'Active') {
                                activeCombos.push({
                                    id: courseId,
                                    series: series,
                                    includedTests: series.Contains_ids
                                });
                            }
                        }
                    }
                }
                
                // Helper function to check if a test is in an active combo
                const isInActiveCombo = (testId) => {
                    return activeCombos.some(combo => combo.includedTests.includes(testId));
                };
                
                for (const courseId of purchasedCourseIds) {
                    try {
                        const txDocId = `${currentUser.uid}_${courseId}`;
                        const txDocSnap = await getDoc(doc(transactionsCollection, txDocId));
                        if (txDocSnap.exists()) {
                            const tx = txDocSnap.data();
                            const series = findSeriesById(courseId);
                            const purchasedDate = parsePurchaseDate(tx);
                            
                            // Check if this individual test is part of an active combo
                            const purchasedViaCombo = !series?.Contains_ids && isInActiveCombo(courseId);
                            const comboForThisTest = activeCombos.find(combo => combo.includedTests.includes(courseId));
                            const validityYears = purchasedViaCombo ? (comboForThisTest?.series?.ValidityYears || 2) : (series?.ValidityYears || 1);
                            const { status, expiryDate } = calculateTestStatus(purchasedDate, validityYears);
                            
                            purchases.push({
                                id: txDocId,
                                courseId: courseId,
                                courseName: series?.title || courseId,
                                courseLink: series?.link || '/test-series',
                                amount: tx.amount || 0,
                                purchasedDate,
                                expiryDate,
                                status,
                                validityYears,
                                purchasedViaCombo,
                                courseSeries: series
                            });
                        }
                    } catch (txError) {
                        console.warn(`Could not fetch transaction for course ${courseId}:`, txError);
                    }
                }
                
                setPurchasedCourses(purchases);
            } catch (error) {
                console.error('Error loading purchased courses:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPurchasedCourses();
    }, [currentUser]);

    // Invoice PDF generator (copied from detail page, but adapted for card data)
    const handleDownloadInvoice = async (course) => {
        setDownloadingInvoice(course.id);
        setToast({ message: 'Generating invoice...', type: 'info' });
        
        // Fetch transaction details for this course
        const txDocId = `${currentUser.uid}_${course.courseId}`;
        const txDocSnap = await getDoc(doc(collection(db, 'transactions'), txDocId));
        if (!txDocSnap.exists()) {
            setToast({ message: 'Invoice not found', type: 'error' });
            setDownloadingInvoice(null);
            return;
        }
        const tx = txDocSnap.data();
        const userNameFinal = currentUser.displayName || '';
        const inv = {
            id: txDocId,
            date: { toDate: () => tx.date?.toDate ? tx.date.toDate() : new Date(tx.date) },
            userEmail: currentUser.email,
            userId: currentUser.uid,
            courseTitle: course.courseName,
            amount: tx.amount,
            paymentId: tx.paymentId || txDocId
        };
        const docPDF = new jsPDF();
        const generatePDF = (logoBase64) => {
            docPDF.addImage(logoBase64, 'JPEG', 15, 15, 30, 25);
            docPDF.setFontSize(22);
            docPDF.setTextColor(30, 58, 138); 
            docPDF.text('TAX INVOICE', 195, 25, { align: 'right' });
            docPDF.setFontSize(10);
            docPDF.setTextColor(100);
            docPDF.text(`Invoice No: #INV-${String(inv.id).substring(0,8).toUpperCase()}`, 195, 32, { align: 'right' });
            docPDF.text(`Date: ${inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A'}`, 195, 37, { align: 'right' });
            // PAID Stamp
            docPDF.setDrawColor(34, 197, 94);
            docPDF.setLineWidth(1);
            docPDF.rect(160, 45, 35, 12);
            docPDF.setTextColor(34, 197, 94);
            docPDF.setFontSize(14);
            docPDF.setFont('helvetica', 'bold');
            docPDF.text('PAID', 177.5, 53, { align: 'center' });
            docPDF.setTextColor(0);
            docPDF.setFontSize(11);
            docPDF.text('PrrepX Pathshala Private Limited', 15, 55);
            docPDF.setFont('helvetica', 'normal');
            docPDF.setFontSize(9);
            docPDF.text(['CIN: U85306UP2025PTC238181', 'GSTIN: 09AAQCP4420J1Z2', 'Lucknow, UP-226301'], 15, 60);
            docPDF.setFont('helvetica', 'bold');
            docPDF.text('BILL TO:', 15, 80);
            docPDF.setFont('helvetica', 'normal');
            docPDF.text([`Name: ${userNameFinal}`, `Email: ${inv.userEmail}`, `User ID: ${inv.userId}`], 15, 85);
            // Table header
            docPDF.setFillColor(37, 99, 235);
            docPDF.rect(15, 105, 180, 10, 'F');
            docPDF.setFont('helvetica', 'bold');
            docPDF.setFontSize(11);
            docPDF.setTextColor(255);
            docPDF.text('Description', 20, 112);
            docPDF.text('Qty', 120, 112);
            docPDF.text('Unit Price', 140, 112);
            docPDF.text('Total', 170, 112);
            // Row background
            docPDF.setFillColor(248, 250, 252);
            docPDF.rect(15, 115, 180, 10, 'F');
            docPDF.setFont('helvetica', 'normal');
            docPDF.setFontSize(10);
            docPDF.setTextColor(0);
            docPDF.text(inv.courseTitle, 20, 122);
            docPDF.text('1', 120, 122);
            docPDF.text(`INR ${inv.amount}`, 140, 122);
            docPDF.text(`INR ${inv.amount}`, 170, 122);
            const finalY = 137;
            docPDF.setFontSize(12);
            docPDF.setFont('helvetica', 'bold');
            docPDF.text(`Total Amount: INR ${inv.amount}`, 195, finalY, { align: 'right' });
            docPDF.setFontSize(9);
            docPDF.setFont('helvetica', 'normal');
            docPDF.text(`Payment ID: ${inv.paymentId}`, 15, finalY);
            docPDF.text('This is a computer generated invoice and requires no signature.', 105, 285, { align: 'center' });
            const firstName = userNameFinal.split(' ')[0] || 'User';
            docPDF.save(`invoice_${firstName}_${inv.paymentId}.pdf`);
            setToast({ message: 'Invoice downloaded successfully!', type: 'success' });
            setDownloadingInvoice(null);
        };
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoImg;
        img.onload = function () {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/jpeg');
            generatePDF(dataURL);
        };
    };

    if (loading) {
        return (
            <div className="pt-container">
                <div className="pt-loading">Loading your purchased tests...</div>
            </div>
        );
    }

    // Separate combo and non-combo purchases
    const comboPurchases = purchasedCourses.filter(c => findSeriesById(c.courseId)?.Contains_ids);
    const comboIds = comboPurchases.map(c => c.courseId);
    const nonComboPurchases = purchasedCourses.filter(c => !comboIds.includes(c.courseId) && !comboPurchases.some(combo => findSeriesById(combo.courseId)?.Contains_ids?.includes(c.courseId)));

    return (
        <div className="pt-container">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
            {/* Header */}
            <div className="pt-header">
                <button className="pt-back-btn" onClick={() => navigate('/profile')}>
                    <FaArrowLeft /> Back to Profile
                </button>
                <h1>My Purchased Tests 📚</h1>
            </div>

            {/* Purchased Courses Grid */}
            <div className="pt-courses-section">
                {purchasedCourses.length === 0 ? (
                    <div className="pt-no-purchases">
                        <p>You haven't purchased any premium test series yet.</p>
                        <button onClick={() => setShowBrowseModal(true)} className="pt-browse-btn">
                            Browse Available Tests
                        </button>
                        {showBrowseModal && (
                            <div className="pt-browse-modal-overlay" onClick={() => setShowBrowseModal(false)}>
                                <div className="pt-browse-modal" onClick={e => e.stopPropagation()}>
                                    <h3 className="pt-browse-modal-title">Choose a Test Series</h3>
                                    <p className="pt-browse-modal-sub">Select the premium test series you want to explore</p>
                                    <div className="pt-browse-modal-btns">
                                        <button
                                            className="pt-browse-modal-btn pt-browse-jee"
                                            onClick={() => navigate('/test-series/jee-premium')}
                                        >
                                            <span className="pt-browse-modal-icon">⚛️</span>
                                            <span className="pt-browse-modal-label">JEE Premium Tests</span>
                                            <span className="pt-browse-modal-arrow">→</span>
                                        </button>
                                        <button
                                            className="pt-browse-modal-btn pt-browse-neet"
                                            onClick={() => navigate('/test-series/neet-premium')}
                                        >
                                            <span className="pt-browse-modal-icon">🧬</span>
                                            <span className="pt-browse-modal-label">NEET Premium Tests</span>
                                            <span className="pt-browse-modal-arrow">→</span>
                                        </button>
                                    </div>
                                    <button className="pt-browse-modal-close" onClick={() => setShowBrowseModal(false)}>✕ Close</button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="pt-courses-grid">
                        {/* Combo Packs as main grid with sub-grids */}
                        {comboPurchases.map((combo) => {
                            const comboSeries = findSeriesById(combo.courseId);
                            return (
                                <div key={combo.id} className={`pt-course-card combo-card ${combo.status === 'Expired' ? 'expired' : 'active'}`}>
                                    {/* Premium Combo Header */}
                                    <div className="pt-combo-premium-header">
                                        <div className="pt-combo-title-section">
                                            <div className="pt-combo-badge-wrapper">
                                                <span className="pt-combo-premium-badge">💎 COMBO PACK</span>
                                                <span className={`pt-status-badge-new ${combo.status.toLowerCase()}`}>
                                                    {combo.status === 'Active' ? '● Active' : '● Expired'}
                                                </span>
                                            </div>
                                            <h3 className="pt-combo-main-title">{comboSeries?.title || combo.courseName}</h3>
                                        </div>
                                        <button
                                            className={`pt-invoice-icon-btn-new ${downloadingInvoice === combo.id ? 'downloading' : ''}`}
                                            onClick={() => handleDownloadInvoice(combo)}
                                            title="Download Invoice"
                                            disabled={downloadingInvoice === combo.id}
                                        >
                                            {downloadingInvoice === combo.id ? (
                                                <span className="spinner"></span>
                                            ) : (
                                                <FaDownload />
                                            )}
                                        </button>
                                    </div>

                                    {/* Purchase Details */}
                                    <div className="pt-combo-purchase-info">
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Purchased</span>
                                            <span className="pt-info-value">{combo.purchasedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                        </div>
                                        <div className="pt-info-divider"></div>
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Expires On</span>
                                            <span className="pt-info-value">{combo.expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                        </div>
                                        <div className="pt-info-divider"></div>
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Amount Paid</span>
                                            <span className="pt-info-value pt-amount">₹{combo.amount.toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>

                                    {/* Included Test Series Section */}
                                    <div className="pt-combo-content">
                                        <div className="pt-included-series-header">
                                            <span className="pt-series-count">{comboSeries?.Contains_ids?.length || 0} Test Series Included</span>
                                            <span className="pt-validity-highlight">2 Years Validity Each 🎯</span>
                                        </div>

                                        <div className="pt-combo-subgrid-wrapper">
                                            {comboSeries?.Contains_ids?.map((id) => {
                                                const s = findSeriesById(id);
                                                return (
                                                    <div key={id} className="pt-combo-subgrid">
                                                        <div className="pt-subgrid-header">
                                                            <h4 className="pt-combo-individual-title">{s?.title}</h4>
                                                            <div className="pt-validity-badge-new">
                                                                Validity: <span className="pt-validity-original">1Y</span>
                                                                <svg className="pt-arrow-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                    <path d="M3 8h10M10 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                                <span className="pt-validity-upgraded">2Y</span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className={`pt-combo-access-btn-new ${combo.status === 'Expired' ? 'disabled' : ''}`}
                                                            disabled={combo.status === 'Expired'}
                                                            onClick={() => navigate(`/purchased-tests/${s?.id}`, {
                                                                state: {
                                                                    comboInfo: {
                                                                        comboId: combo.courseId,
                                                                        comboTitle: comboSeries?.title || combo.courseName,
                                                                        purchasedDate: combo.purchasedDate,
                                                                        expiryDate: combo.expiryDate,
                                                                        amount: combo.amount
                                                                    }
                                                                }
                                                            })}
                                                        >
                                                            {combo.status === 'Expired' ? (
                                                                <span>🔒 Expired</span>
                                                            ) : (
                                                                <>
                                                                    <span>Access Tests</span>
                                                                    <FaArrowRight />
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {/* Individual purchases with premium design */}
                        {nonComboPurchases.map((course) => {
                            const courseSeries = findSeriesById(course.courseId);
                            return (
                                <div key={course.id} className={`pt-course-card individual-card ${course.status === 'Expired' ? 'expired' : 'active'}`}>
                                    {/* Premium Individual Header */}
                                    <div className="pt-individual-premium-header">
                                        <div className="pt-individual-title-section">
                                            <div className="pt-individual-badge-wrapper">
                                                <span className="pt-individual-premium-badge">📚 TEST SERIES</span>
                                                <span className={`pt-status-badge-new ${course.status.toLowerCase()}`}>
                                                    {course.status === 'Active' ? '● Active' : '● Expired'}
                                                </span>
                                            </div>
                                            <h3 className="pt-individual-main-title">{course.courseName}</h3>
                                        </div>
                                        <button
                                            className={`pt-invoice-icon-btn-new ${downloadingInvoice === course.id ? 'downloading' : ''}`}
                                            onClick={() => handleDownloadInvoice(course)}
                                            title="Download Invoice"
                                            disabled={downloadingInvoice === course.id}
                                        >
                                            {downloadingInvoice === course.id ? (
                                                <span className="spinner"></span>
                                            ) : (
                                                <FaDownload />
                                            )}
                                        </button>
                                    </div>

                                    {/* Purchase Details */}
                                    <div className="pt-individual-purchase-info">
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Purchased</span>
                                            <span className="pt-info-value">{course.purchasedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                        </div>
                                        <div className="pt-info-divider"></div>
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Expires On</span>
                                            <span className="pt-info-value">{course.expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                        </div>
                                        <div className="pt-info-divider"></div>
                                        <div className="pt-info-item">
                                            <span className="pt-info-label">Amount Paid</span>
                                            <span className="pt-info-value pt-amount">₹{course.amount.toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>

                                    {/* Validity Info */}
                                    <div className="pt-individual-content">
                                        <div className="pt-individual-validity-section">
                                            <div className="pt-validity-info">
                                                <span className="pt-validity-label">Validity Period</span>
                                                <div className="pt-validity-badge-individual">
                                                    <span className="pt-validity-duration">
                                                        {course.purchasedViaCombo ? '2 Years' : `${course.validityYears} Year${course.validityYears > 1 ? 's' : ''}`}
                                                        {course.purchasedViaCombo && ' (via Combo)'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Access Button */}
                                        <button
                                            className={`pt-individual-access-btn ${course.status === 'Expired' ? 'disabled' : ''}`}
                                            onClick={() => navigate(`/purchased-tests/${course.courseId}`)}
                                            disabled={course.status === 'Expired'}
                                        >
                                            {course.status === 'Expired' ? (
                                                <span>🔒 Expired - Renew Access</span>
                                            ) : (
                                                <>
                                                    <span>Access All Tests</span>
                                                    <FaArrowRight />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Summary Section */}
            {purchasedCourses.length > 0 && (
                <div className="pt-summary">
                    <div className="pt-summary-card">
                        <h4>Active Subscriptions</h4>
                        <p className="pt-summary-value">
                            {purchasedCourses.filter(c => c.status === 'Active').length}
                        </p>
                    </div>
                    <div className="pt-summary-card">
                        <h4>Total Courses</h4>
                        <p className="pt-summary-value">{purchasedCourses.length}</p>
                    </div>
                    <div className="pt-summary-card">
                        <h4>Total Amount Paid</h4>
                        <p className="pt-summary-value">₹{purchasedCourses.reduce((sum, c) => sum + parseInt(c.amount || 0), 0)}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchasedTestsPage;
